import React from 'react';

import { type UseQueryOptions, useQuery } from '@lumapps/base-api/react-query';
import { ServerListResponse } from '@lumapps/base-api/types';
import { communitiesQueryKeys, list } from '@lumapps/communities/api';
import { type CommunitiesListRequestParams, Community, RenderingType } from '@lumapps/communities/types';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { useSelector } from '@lumapps/redux/react';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';

/**
 * Hook to resolve a list of spaces (communities with the `space` rendering type) from their IDs.
 *
 * @returns the resolved spaces as `items` and the `isLoading` state.
 */
export const useQuerySpacesByIds = (
    { ids }: { ids: string[] },
    options?: UseQueryOptions,
): { items: Community[]; isLoading: boolean } => {
    const lang = useSelector(currentLanguageSelector);
    const instanceId = useSelector(instanceIdSelector);

    const params = React.useMemo<Partial<CommunitiesListRequestParams>>(
        () => ({
            ids,
            instanceId: [instanceId],
            lang,
            renderingTypes: [RenderingType.space],
            maxResults: ids.length,
        }),
        [ids, instanceId, lang],
    );

    const { data, isInitialLoading } = useQuery<ServerListResponse<Community>>({
        ...options,
        enabled: ids.length > 0 && (options?.enabled ?? true),
        queryKey: communitiesQueryKeys.list(params),
        queryFn: async () => {
            const { data: response } = await list(params);

            return response;
        },
    });

    return { items: data?.items ?? [], isLoading: isInitialLoading };
};
